import { Matcher } from '@chronosphereio/editors/prometheus';
import { useQuery } from '@tanstack/react-query';
import { useBuilderContext } from './promql-builder-context';

/**
 * Labels we don't need to show to user.
 * __name__ is a special label that represents the metric name: https://prometheus.io/docs/prometheus/latest/configuration/template_reference/#data-structures
 */
export const LABELS_TO_REMOVE = new Set(['__name__']);

export function useMetricMetadata(metric: string) {
  const { promClient } = useBuilderContext();
  return useQuery(['metric-metadata', metric], async () => {
    return (await promClient?.metadataClient.getMetricInfo(metric)) ?? { type: undefined, isClassicHistogram: false };
  });
}

export function useMetricNames() {
  const { promClient } = useBuilderContext();
  return useQuery(['metric-names'], async () => {
    const metrics = (await promClient?.metricNames()) ?? [];
    return metrics.sort((a, b) => a.localeCompare(b));
  });
}

export function useLabelNames(metricName: string) {
  const { promClient } = useBuilderContext();
  return useQuery(['label-names', metricName], async () => {
    const labels = (await promClient?.labelNames(metricName)) ?? [];
    return labels.filter((label) => !LABELS_TO_REMOVE.has(label));
  });
}

export function useLabelValues(labelName: string, metricName: string, matchers?: Matcher[]) {
  const { promClient } = useBuilderContext();
  return useQuery(
    ['label-values', labelName, metricName, matchers],
    async () => {
      return (await promClient?.labelValues(labelName, metricName, matchers)) ?? [];
    },
    {
      // Only fetch when a label is selected. Otherwise, the endpoint will 404.
      enabled: !!labelName,
    }
  );
}
